<script setup lang="ts">
import { ref, onMounted } from 'vue'

defineProps<{
  src: string
  alt?: string
  size?: 'sm' | 'card' | 'md' | 'lg' | 'full'
  bg?: string
  grayscale?: boolean
}>()

const loaded = ref(false)
const slow = ref(false)

onMounted(() => {
  setTimeout(() => {
    if (!loaded.value) {
      slow.value = true
    }
  }, 50)
})
</script>

<template>
  <div class="styled-image" :class="[size ?? 'md', bg]">
    <div v-if="!loaded" class="placeholder" />
    <img :src="src" :alt="alt ?? ''" class="w-full h-full object-cover" :class="{ 'opacity-0': !loaded, 'fade-in': slow, 'grayscale': grayscale }" @load="loaded = true" />
  </div>
</template>

<style scoped lang="scss">
.styled-image {
  position: relative;
  border-radius: 0.375rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 0.375rem;
    pointer-events: none;
    box-shadow: inset 0 0 25px rgba(0, 0, 0, 0.25);
  }

  // Size variants
  &.sm {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.25rem;
    box-shadow:
      0 1px 4px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);

    &::after {
      border-radius: 0.25rem;
      box-shadow: inset 0 0 8px rgba(0, 0, 0, 0.2);
    }
  }

  &.card {
    width: 5rem;
    height: 5rem;
    box-shadow:
      0 2px 5px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.05);

    &::after {
      box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.2);
    }
  }

  &.md {
    width: 8rem;
    height: 8rem;
  }

  &.lg {
    width: 16rem;
    height: 16rem;
  }

  &.full {
    width: 100%;
    height: 100%;
  }

  .placeholder {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle, rgba(60, 70, 90, 0.6) 0%, rgba(30, 35, 45, 0.8) 70%, rgba(20, 25, 35, 0.9) 100%);
  }

  img.fade-in {
    transition: opacity 150ms ease-in;
  }
}
</style>
